import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import MinimisedSection from '../../../../../../../../components/MinimisedSection'
import MinimisedSectionHeader from '../../../../../../../../components/MinimisedSection/MinimisedSectionHeader'
import Field from '../../../../../../../../components/Form/Field'
import FieldLabelWrapper from '../../../../../../../../features/components/Form/FieldLabelWrapper'

import {
  AVERAGE_DECREASED_ANXIETY_RATING,
  AVERAGE_HAPPINESS_RATING,
  AVERAGE_LIFE_SATISFACTION_RATING,
  AVERAGE_PURPOSE_OF_LIFE_RATING,
  BAME_PERCENTAGE,
  DECREASED_ANXIETY_PERCENTAGE,
  FEMALE_PERCENTAGE,
  HAPPINESS_PERCENTAGE,
  LIFE_SATISFACTION_PERCENTAGE,
  MALE_PERCENTAGE,
  PURPOSE_OF_LIFE_PERCENTAGE,
  REGION,
  regions,
  STAKEHOLDER_COUNT,
  STAKEHOLDER_TYPE,
  STAKEHOLDERS_LIST,
  stakeholderTypeOptions,
  UNKNOWN_ETHNICITY_PERCENTAGE,
  UNKNOWN_GENDER_PERCENTAGE,
  WHITE_PERCENTAGE
} from '../../../fields'

import useStyles from './styles'

const StakeholderSection = ({ formik, index, isSelected, onSelect, onClose, onDelete }) => {
  const { t } = useTranslation()

  const classes = useStyles()

  const { values } = formik

  const availableStakeholderTypeOptions = useMemo(() => {
    const selectedStakeholderTypes = values[STAKEHOLDERS_LIST].map(assumption => assumption[STAKEHOLDER_TYPE])

    return stakeholderTypeOptions.filter(
      option =>
        !selectedStakeholderTypes.includes(option.value) ||
        option.value === values[STAKEHOLDERS_LIST][index][STAKEHOLDER_TYPE]
    )
  }, [values, index])

  const onClickHandler = useCallback(() => {
    if (isSelected) {
      onClose()
    } else {
      onSelect()
    }
  }, [isSelected, onSelect, onClose])

  return (
    <MinimisedSection
      headerContent={<MinimisedSectionHeader title={t(`Stakeholder ${index + 1}`)} onDelete={onDelete} />}
      isOpened={isSelected}
      onHeaderClick={onClickHandler}
    >
      <Field
        name={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_TYPE}]`}
        formik={formik}
        options={availableStakeholderTypeOptions}
        placeholder="Stakeholder Type"
        className={classes.stakeholderTypeSelect}
      />
      <FieldLabelWrapper
        label={t('Stakeholder Count')}
        labelFor={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_COUNT}]`}
      >
        <Field
          placeholder={t('Stakeholder Count')}
          formik={formik}
          name={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_COUNT}]`}
        />
      </FieldLabelWrapper>
      <FieldLabelWrapper
        label={t('Stakeholder Count')}
        labelFor={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_COUNT}]`}
      >
        <Field
          placeholder={t('Stakeholder Count')}
          formik={formik}
          name={`${STAKEHOLDERS_LIST}[${index}][${STAKEHOLDER_COUNT}]`}
        />
      </FieldLabelWrapper>
      <FieldLabelWrapper label={t('Region')} labelFor={`${STAKEHOLDERS_LIST}[${index}][${REGION}]`}>
        <Field
          placeholder={t('Region')}
          formik={formik}
          name={`${STAKEHOLDERS_LIST}[${index}][${REGION}]`}
          options={regions}
        />
      </FieldLabelWrapper>

      <div className={classes.rowWith3Items}>
        <FieldLabelWrapper label={t('Male %')} labelFor={`${STAKEHOLDERS_LIST}[${index}][${MALE_PERCENTAGE}]`}>
          <Field
            className={classes.fieldInRow}
            formik={formik}
            name={`${STAKEHOLDERS_LIST}[${index}][${MALE_PERCENTAGE}]`}
          />
        </FieldLabelWrapper>
        <FieldLabelWrapper label={t('Female %')} labelFor={`${STAKEHOLDERS_LIST}[${index}][${FEMALE_PERCENTAGE}]`}>
          <Field
            className={classes.fieldInRow}
            formik={formik}
            name={`${STAKEHOLDERS_LIST}[${index}][${FEMALE_PERCENTAGE}]`}
          />
        </FieldLabelWrapper>
        <FieldLabelWrapper
          label={t('Unknown %')}
          labelFor={`${STAKEHOLDERS_LIST}[${index}][${UNKNOWN_GENDER_PERCENTAGE}]`}
        >
          <Field
            className={classes.fieldInRow}
            formik={formik}
            name={`${STAKEHOLDERS_LIST}[${index}][${UNKNOWN_GENDER_PERCENTAGE}]`}
          />
        </FieldLabelWrapper>
      </div>

      <div className={classes.rowWith3Items}>
        <FieldLabelWrapper label={t('White %')} labelFor={`${STAKEHOLDERS_LIST}[${index}][${WHITE_PERCENTAGE}]`}>
          <Field
            className={classes.fieldInRow}
            formik={formik}
            name={`${STAKEHOLDERS_LIST}[${index}][${WHITE_PERCENTAGE}]`}
          />
        </FieldLabelWrapper>
        <FieldLabelWrapper label={t('BAME %')} labelFor={`${STAKEHOLDERS_LIST}[${index}][${BAME_PERCENTAGE}]`}>
          <Field
            className={classes.fieldInRow}
            formik={formik}
            name={`${STAKEHOLDERS_LIST}[${index}][${BAME_PERCENTAGE}]`}
          />
        </FieldLabelWrapper>
        <FieldLabelWrapper
          label={t('Unknown %')}
          labelFor={`${STAKEHOLDERS_LIST}[${index}][${UNKNOWN_ETHNICITY_PERCENTAGE}]`}
        >
          <Field
            className={classes.fieldInRow}
            formik={formik}
            name={`${STAKEHOLDERS_LIST}[${index}][${UNKNOWN_ETHNICITY_PERCENTAGE}]`}
          />
        </FieldLabelWrapper>
      </div>

      <div className={classes.rowWith2Items}>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Life Satisfaction %')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${LIFE_SATISFACTION_PERCENTAGE}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${LIFE_SATISFACTION_PERCENTAGE}]`}
            />
          </FieldLabelWrapper>
        </div>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Av. Life Satisf. Rating')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_LIFE_SATISFACTION_RATING}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_LIFE_SATISFACTION_RATING}]`}
            />
          </FieldLabelWrapper>
        </div>
      </div>

      <div className={classes.rowWith2Items}>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Purpose of Life %')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${PURPOSE_OF_LIFE_PERCENTAGE}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${PURPOSE_OF_LIFE_PERCENTAGE}]`}
            />
          </FieldLabelWrapper>
        </div>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Av. Purp. of Life Rating')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_PURPOSE_OF_LIFE_RATING}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_PURPOSE_OF_LIFE_RATING}]`}
            />
          </FieldLabelWrapper>
        </div>
      </div>

      <div className={classes.rowWith2Items}>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Happiness %')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${HAPPINESS_PERCENTAGE}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${HAPPINESS_PERCENTAGE}]`}
            />
          </FieldLabelWrapper>
        </div>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Av. Happiness Rating')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_HAPPINESS_RATING}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_HAPPINESS_RATING}]`}
            />
          </FieldLabelWrapper>
        </div>
      </div>

      <div className={classes.rowWith2Items}>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Decr. Anxiety %')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${DECREASED_ANXIETY_PERCENTAGE}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${DECREASED_ANXIETY_PERCENTAGE}]`}
            />
          </FieldLabelWrapper>
        </div>
        <div className={classes.col}>
          <FieldLabelWrapper
            label={t('Av. Decr. Anxiety Rating')}
            labelFor={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_DECREASED_ANXIETY_RATING}]`}
          >
            <Field
              className={classes.fieldInRow}
              formik={formik}
              name={`${STAKEHOLDERS_LIST}[${index}][${AVERAGE_DECREASED_ANXIETY_RATING}]`}
            />
          </FieldLabelWrapper>
        </div>
      </div>
    </MinimisedSection>
  )
}

StakeholderSection.propTypes = {
  formik: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

export default StakeholderSection
